import './DodConditionBuilder.scss';
import {DodFactSet, FactCondition, ProductFilterCondition, SummedSelection, TimePeriodValue} from "@/types/DodRun";
import {RunConfigMarket} from "@/types/ReportRun";
import React, {ChangeEvent, useMemo} from "react";
import {WithUid} from "@/types/WithUid";
import {ByzzerButton, ByzzerChangeEventHandler, ByzzerConditionInput, ByzzerSelect, ByzzerSelectOption} from "@byzzer/ui-components";
import {factSetToDisplayNames, getAutoConditionName} from "@/components/DodConfigEditor/common/utils";
import {nanoid} from "nanoid";
import {clone} from "lodash";
import {ByzzerSwitch} from "@/components/form";
import {ByzzerInput} from "@/components/form";
import classnames from "classnames";
import {TimePeriodRange} from "@/utils/timePeriod/TimePeriodRange";

export interface DodConditionEditorProps {
    className?: string;
    value: FactCondition[];
    facts: DodFactSet[];
    onChange?: ByzzerChangeEventHandler<FactCondition[]>;
    minConditions?: number;
    maxConditions?: number;
    markets?: RunConfigMarket[];
    summedMarkets?: SummedSelection<RunConfigMarket[]>[];
    conditionMarket?: string;
    onMarketChange?: (market: string) => void;
    timePeriods?: TimePeriodValue[];
    summedTimePeriods?: SummedSelection[];
    conditionTimePeriod?: string;
    onTimePeriodChange?: (timePeriod: string) => void;
    maxDate?: string;
    conditionName?: string;
    onConditionNameChange?: (name: string) => void;
    onAutoConditionNameChange?: (name: string) => void;
    productFilter?: ProductFilterCondition;
    onProductFilterChange?: (productFilter: ProductFilterCondition | undefined) => void;
}

interface DodConditionalBuilderValue {
    display?: string;
    value?: number | string;
    factId?: number | string;
    factType?: string;
    format: string;
}

const COMPARATOR_OPERATORS = [
    {
        display: 'Greater Than (>)',
        value: 'gt',
    },
    {
        display: 'Greater Than Or Equal To (≥)',
        value: 'gte',
    },
    {
        display: 'Less Than (<)',
        value: 'lt',
    },
    {
        display: 'Less Than Or Equal To (≤)',
        value: 'lte',
    },
    {
        display: 'Equal To (=)',
        value: 'eq',
    },
    {
        display: 'Not Equal To (≠)',
        value: 'neq',
    },
    {
        display: 'Between',
        value: 'btw',
    },
    {
        display: 'Not Between',
        value: 'nbtw',
    },
];

const DATE_COMPARATOR_OPERATORS = [
    {
        display: 'Before',
        value: 'before',
    },
    {
        display: 'After',
        value: 'after',
    },
    {
        display: 'Between',
        value: 'btw',
    },
];

const BLANK_FACT_CONDITION: FactCondition = {
    fact: '',
    comparator: 'gt',
    value: '',
    condition: 'and',
};

const baseClassName = 'dod-condition-builder';

export function DodConditionEditor(
    {
        className,
        value,
        facts,
        onChange,
        minConditions = 1,
        maxConditions = Infinity,
        markets,
        summedMarkets,
        conditionMarket,
        onMarketChange,
        timePeriods,
        summedTimePeriods,
        conditionTimePeriod,
        onTimePeriodChange,
        maxDate,
        conditionName: conditionNameProp,
        onConditionNameChange,
        onAutoConditionNameChange,
        productFilter,
        onProductFilterChange,
    }: DodConditionEditorProps) {

    const selectedProductMode: 'All' | 'Top' | 'Bottom' = productFilter?.mode ?? 'All';
    const [isConditionsExpanded, setIsConditionsExpanded] = React.useState<boolean>(selectedProductMode === 'All' || value.some(condition => condition.fact !== ''));
    const [countInput, setCountInput] = React.useState<string>(String(productFilter?.count ?? 10));

    React.useEffect(() => {
        if (productFilter?.mode !== 'All' && productFilter?.count !== undefined) {
            setCountInput(String(productFilter.count));
        }
    }, [productFilter?.count]);

    const conditions = useMemo<WithUid<FactCondition, string>[]>(() => {
        if (value.length) {
            return value.map((condition, uid) => ({
                value: condition,
                uid: `${uid}`,
            }));
        }

        return [createDefaultCondition()];
    }, [value]);

    const factOptions = useMemo<ByzzerSelectOption[]>(() => {
        return facts.map(factSetToDisplayNames).flat().map((fact) => ({
            display: fact,
            value: fact,
        }))
    }, [facts]);

    const productModeOptions = useMemo<ByzzerSelectOption[]>(() => {
        return [
            {display: 'All', value: 'All'},
            {display: 'Top', value: 'Top'},
            {display: 'Bottom', value: 'Bottom'},
        ];
    }, []);

    const marketOptions = useMemo<ByzzerSelectOption[]>(() => {
        const options: ByzzerSelectOption[] = [
            { display: 'Each Market', value: 'each_market' },
        ];
        (markets ?? []).forEach((m) => {
            if (m.name) options.push({ display: m.name, value: m.key ?? m.name });
        });
        (summedMarkets ?? []).forEach((s) => {
            if (s.name) options.push({ display: s.name, value: s.name });
        });
        return options;
    }, [markets, summedMarkets]);

    const timePeriodOptions = useMemo<ByzzerSelectOption[]>(() => {
        const options: ByzzerSelectOption[] = [
            { display: 'Each Time Period', value: 'each_time_period' },
        ];
        (timePeriods ?? []).flat().forEach((tp) => {
            try {
                const display = new TimePeriodRange(tp, maxDate).toLegacyDodString();
                options.push({ display, value: tp });
            } catch {
                // skip invalid time period values
            }
        });
        (summedTimePeriods ?? []).forEach((s) => {
            if (s.name) options.push({ display: s.name, value: s.name });
        });
        return options;
    }, [timePeriods, summedTimePeriods, maxDate]);

    const autoConditionName = useMemo<string>(() => {
        const runConfigForConditionName = {
            filters: {
                markets: { values: markets ?? [], summedSelections: summedMarkets ?? [] },
                timePeriods: { values: timePeriods ?? [], summedSelections: summedTimePeriods ?? [] },
            },
            conditions: {
                facts: value,
                market: conditionMarket ?? 'each_market',
                timePeriod: conditionTimePeriod ?? 'each_time_period',
                products: productFilter ?? { mode: 'All' },
            },
        };

        return getAutoConditionName(runConfigForConditionName, maxDate);
    }, [value, conditionMarket, conditionTimePeriod, productFilter, markets, summedMarkets, timePeriods, summedTimePeriods, maxDate]);

    // Notify parent of auto-generated name so it can be used as fallback on Apply
    React.useEffect(() => {
        if (!conditionNameProp) {
            onAutoConditionNameChange?.(autoConditionName);
        }
    }, [autoConditionName, conditionNameProp, onAutoConditionNameChange]);

    const conditionName = conditionNameProp ?? autoConditionName;

    function getSelectedFacts(fact, selectedFacts): DodConditionalBuilderValue | undefined {
        let tempFactsValues: DodConditionalBuilderValue[] = []
        if (fact.fact) {
            tempFactsValues.push({
                display: fact.display,
                value: fact.display,
                factId: fact.id,
                factType: 'direct',
                format: fact?.format,
            })
        }
        if (fact.yearAgo) {
            tempFactsValues.push({
                display: fact.display + ' Year-Ago',
                value: fact.display + ' Year-Ago',
                factId: fact.id,
                factType: 'ya',
                format: fact?.format,
            })
        }
        if (fact.changeVsYearAgo) {
            tempFactsValues.push({
                display: fact.display + ` Change vs Year-Ago`,
                value: fact.display + ` Change vs Year-Ago`,
                factId: fact.id,
                factType: 'cngYa',
                format: fact?.format,
            })
        }
        if (fact.percentChangeVsYearAgo) {
            tempFactsValues.push({
                display: fact.display + ` % Change vs Year-Ago`,
                value: fact.display + ` % Change vs Year-Ago`,
                factId: fact.id,
                factType: 'percentChangeVsYearAgo',
                format: fact?.format,
            })
        }
        return tempFactsValues.find((fact) => fact.display === selectedFacts);
    }

    function getFormattedFactsValue(selectedFacts) {
        let getObj: DodConditionalBuilderValue = {display: '', value: '', factId: '', factType: '', format: '',}
        facts.forEach((fact) => {
            if (factSetToDisplayNames(fact).includes(selectedFacts)) {
                getObj = getSelectedFacts(fact, selectedFacts) ?? fact
                //logic to get format property for facts (e.g. Total Unit Sales Change vs. Year-Ago) in the required format to add suffix("%") or prefix("$")
            }
        })
        return getObj
    }

    function getFactsValue(selectedFacts) {
        let formattedFactsValue = {
            decimalScale: 0,
            suffix: '',
            prefix: ''
        }
        let decimalScale = 0;
        if (getFormattedFactsValue(selectedFacts).format) {
            let splittedFormat = getFormattedFactsValue(selectedFacts).format.split(';');
            if (splittedFormat[0]) {
                splittedFormat = splittedFormat[0].split('.');
                if (splittedFormat.length >= 2) {
                    var match = splittedFormat[1].match(/^0+/);
                    decimalScale = match ? match[0].length : 0;
                }
            }
        }
        if (selectedFacts) {
            formattedFactsValue['decimalScale'] = getFormattedFactsValue(selectedFacts).format.includes('.') ? decimalScale : 0
            formattedFactsValue['prefix'] = getFormattedFactsValue(selectedFacts).factType !== 'percentChangeVsYearAgo' && getFormattedFactsValue(selectedFacts).format.includes("$") ? '$' : ''
            formattedFactsValue['suffix'] = getFormattedFactsValue(selectedFacts).factType === 'percentChangeVsYearAgo' || getFormattedFactsValue(selectedFacts).format.includes('%') ? '%' : ''
        }
        return formattedFactsValue
    }

    function handleConditionChange(e, uid: string) {
        const {
            value: {key, operation, value},
        } = e;
        const isRange = operation === 'btw' || operation === 'nbtw';
        const conditionIndex = conditions.findIndex((condition) => uid === condition.uid);
        const updatedConditions: FactCondition[] = conditions.map(v => v.value);
        const previousFact = updatedConditions[conditionIndex]?.fact;
        const wasDate = isLaunchDateFact(previousFact);
        const isDate = isLaunchDateFact(key);
        const factTypeChanged = wasDate !== isDate;

        let sanitizedValue: string | [string, string];
        if (isRange) {
            const vals = Array.isArray(value) ? value : [value];
            sanitizedValue = [
                vals[0] ? (isDate ? vals[0].replace(/[^0-9/]/g, '') : vals[0].replace(/[\\^$*+%(,)|[\]{}]/g, '')) : '',
                vals[1] ? (isDate ? vals[1].replace(/[^0-9/]/g, '') : vals[1].replace(/[\\^$*+%(,)|[\]{}]/g, '')) : '',
            ];
        } else {
            const updatedValue = Array.isArray(value) ? value[0] : value;
            sanitizedValue = updatedValue
                ? isDate
                    ? updatedValue.replace(/[^0-9/]/g, '')
                    : updatedValue.replace(/[\\^$*+%(,)|[\]{}]/g, '')
                : '';
        }

        updatedConditions[conditionIndex] = {
            fact: key,
            comparator: factTypeChanged ? (isDate ? 'after' : 'gt') : operation,
            value: factTypeChanged ? '' : sanitizedValue,
            condition: updatedConditions[conditionIndex]?.condition ?? 'and'
        } as FactCondition
        
        onChange?.({
            value: updatedConditions
        })
    }

    function createDefaultCondition() {
        return {value: clone(BLANK_FACT_CONDITION), uid: nanoid()};
    }

    function add() {
        onChange?.({
            value: [...value, clone(BLANK_FACT_CONDITION)]
        })
    }

    function remove(key: string) {
        const filteredConditions = conditions.filter(condition => key !== condition.uid).map(v => v.value);
        onChange?.({
            value: filteredConditions
        })
    }

    function handleJoinTypeChange(e: ChangeEvent<HTMLInputElement>, uid: string) {
        let updatedFactConditions = conditions?.map((condition, i) => {
            if (uid === condition.uid) {
                condition.value.condition = e.target.checked ? 'or' : 'and'
                return condition
            }
            return condition
        }).map(v => v.value)
        onChange?.({
            value: updatedFactConditions
        })
    }

    function isConditionValid(condition: FactCondition) {
        const isRange = condition.comparator === 'btw' || condition.comparator === 'nbtw';
        const isDateFact = isLaunchDateFact(condition.fact);

        const isValidMmDdYyyyDate = (raw: string): boolean => {
            if (!raw) return false;
            const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (!m) return false;
            const month = Number(m[1]);
            const day = Number(m[2]);
            const year = Number(m[3]);
            const date = new Date(year, month - 1, day);
            return (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day
            );
        };

        if (isRange) {
            const hasRange = Boolean(
                condition.fact &&
                condition.comparator &&
                Array.isArray(condition.value) &&
                condition.value[0] &&
                condition.value[1]
            );

            if (!hasRange) return false;

            if (!isDateFact) return true;

            return Boolean(
                Array.isArray(condition.value) &&
                isValidMmDdYyyyDate(condition.value[0]) &&
                isValidMmDdYyyyDate(condition.value[1])
            );
        }

        if (isDateFact) {
            return Boolean(
                condition.fact &&
                condition.comparator &&
                typeof condition.value === 'string' &&
                isValidMmDdYyyyDate(condition.value)
            );
        }

        return Boolean(
            condition.fact &&
            condition.comparator &&
            typeof condition.value === 'string' &&
            condition.value
        );
    }

    function isLaunchDateFact(fact: string): boolean {
        return fact?.toLowerCase() === 'launch date';
    }

    function handleProductModeChange(mode: 'All' | 'Top' | 'Bottom') {
        if (mode === 'All') {
            setIsConditionsExpanded(true);
            onProductFilterChange?.({ mode: 'All' });
            return;
        }

        setIsConditionsExpanded(false);
        onChange?.({
            value: [],
        });
        onProductFilterChange?.({
            mode,
            count: productFilter?.count ?? 10,
            sortByFact: productFilter?.sortByFact,
        });
        
    }

    function handleProductCountChange(raw: string) {
        if (!productFilter || productFilter.mode === 'All') return;
        const sanitized = raw.replace(/[^0-9]/g, '');
        setCountInput(sanitized);
        if (!sanitized) return;
        const count = Math.max(1, Number.parseInt(sanitized, 10));
        onProductFilterChange?.({...productFilter, count});
    }

    function handleProductCountBlur() {
        if (!productFilter || productFilter.mode === 'All') return;
        if (!countInput) {
            setCountInput(String(productFilter.count ?? 10));
        }
    }


    return <div className={classnames(baseClassName, className)}>
        <div className={`${baseClassName}__product-selector`}>
            <div className={`${baseClassName}__product-selector-column`}>
                <ByzzerSelect
                    className={`${baseClassName}__product-select`}
                    name={'product-filter-mode'}
                    label={'Show'}
                    options={productModeOptions}
                    value={selectedProductMode}
                    allowClear={false}
                    onChange={(e) => handleProductModeChange(e.value as 'All' | 'Top' | 'Bottom')}
                />
            </div>

            {selectedProductMode !== 'All' && (
                <>
                    <div className={`${baseClassName}__product-selector-column`}>
                        <ByzzerInput
                            className={`${baseClassName}__product-count-input`}
                            name={'product-filter-count'}
                            type={'text'}
                            value={countInput}
                            onChange={(e: any) => handleProductCountChange(e.target.value)}
                            label={''}
                            placeholder={'10'}
                            sublabel={undefined}
                            button={undefined}
                            disabled={false}
                            locked={false}
                            onIconClick={undefined}
                            onButtonClick={undefined}
                            onBlur={handleProductCountBlur}
                            required={false}
                        />
                    </div>
                </>
            )}

            {selectedProductMode === 'All' ? (
                <span className={`${baseClassName}__product-selector-dim`}>Products</span>
            ) : (
                <div className={`${baseClassName}__product-based-on-group`}>
                    <span className={`${baseClassName}__product-selector-dim`}>Products based on</span>
                    <ByzzerSelect
                        className={`${baseClassName}__product-based-on-select`}
                        name={'product-filter-sort-by-inline'}
                        label={''}
                        options={factOptions}
                        value={productFilter?.sortByFact ?? ''}
                        placeholder={'Select fact...'}
                        onChange={(e) => {
                            if (!productFilter || productFilter.mode === 'All') return;
                            onProductFilterChange?.({...productFilter, sortByFact: e.value as string});
                        }}
                    />
                </div>
            )}
        </div>

        {selectedProductMode !== 'All' && (
            <div className={`${baseClassName}__add-rule-row`}>
                <ByzzerButton
                    className={`${baseClassName}__add-rule-button`}
                    label={isConditionsExpanded ? 'Add Rule -' : 'Add Rule +'}
                    onClick={() => setIsConditionsExpanded((prev) => !prev)}
                />
            </div>
        )}

        {(selectedProductMode === 'All' || isConditionsExpanded) && (
            <>
                <div className={`${baseClassName}__where-label`}>WHERE</div>
                {conditions.map(({value, uid}, index) => (
                    <React.Fragment key={uid}>

                {index > 0 && (
                    <div className={`${baseClassName}__join-type`}>
                        <div className={classnames(`${baseClassName}__join-type-label`, {
                            [`${baseClassName}__join-type-label--active`]: value.condition === 'and',
                        })}>
                            AND
                        </div>
                        <ByzzerSwitch
                            className={`${baseClassName}__join-toggle`}
                            onChange={(e) => handleJoinTypeChange(e, uid)}
                            value={value.condition === 'or'}
                        />
                        <div className={classnames(`${baseClassName}__join-type-label`, {
                            [`${baseClassName}__join-type-label--active`]: value.condition === 'or',
                        })}>
                            OR
                        </div>
                    </div>
                )}
                {/* @ts-ignore — keyReactSelectOptions, operationReactSelectOptions, valueReactSelectOptions are required in @byzzer/ui-components@1.0.106 but should be optional (fixed in local byzzer-components, pending publish) */}
                <ByzzerConditionInput
                    className={classnames(`${baseClassName}__condition`, {
                        [`${baseClassName}__condition--sans-labels`]: index > 0
                    })}
                    value={{
                        key: value.fact,
                        operation: value.comparator,
                        value: Array.isArray(value.value) ? value.value : [value.value],
                    }}
                    valueSuffix={getFactsValue(value.fact).suffix}
                    valuePrefix={getFactsValue(value.fact).prefix}
                    valueDecimalScale={getFactsValue(value.fact).decimalScale}
                    valueFixedDecimalScale={Boolean(getFactsValue(value.fact).decimalScale)}
                    valueGroupStyle={'thousand'}
                    valueThousandSeperator={true}
                    isValueTextInput={!isLaunchDateFact(value.fact)}
                    isValueDateInput={isLaunchDateFact(value.fact)}
                    keyLabel={'Fact'}
                    operationLabel={'Operator'}
                    valueLabel={'Value'}
                    keyPlaceholder={'Fact'}
                    operationPlaceholder={'Operation'}
                    valuePlaceholder={isLaunchDateFact(value.fact) ? 'MM/DD/YYYY' : 'Value'}
                    allowMultipleValues={value.comparator === 'btw' || value.comparator === 'nbtw'}
                    keyOptions={factOptions}
                    operationOptions={isLaunchDateFact(value.fact) ? DATE_COMPARATOR_OPERATORS : COMPARATOR_OPERATORS}
                    allowOperationClear={false}
                    allowKeyClear={false}
                    onChange={(e) => handleConditionChange(e, uid)}
                    valueOptions={[]}
                    actions={
                        <>
                            <ByzzerButton onClick={() => add()}
                                          disabled={
                                              maxConditions <= conditions.length ||
                                              !isConditionValid(value)
                                          }
                                          label={'+'}
                            />

                            <ByzzerButton
                                onClick={() => remove(uid)}
                                disabled={minConditions >= conditions.length}
                                label={'-'}
                            />
                        </>
                    }
                />
                    </React.Fragment>
                ))}
            </>
        )}
        <div className={`${baseClassName}__in-label`}>IN</div>
        <div className={`${baseClassName}__market-time-row`}>
            <div className={`${baseClassName}__market-selector`}>
                <span className={`${baseClassName}__market-selector-label`}>Market</span>
                <ByzzerSelect
                    className={`${baseClassName}__market-select`}
                    name={'market'}
                    options={marketOptions}
                    value={conditionMarket ?? 'each_market'}
                    placeholder={'Select...'}
                    onChange={(e) => onMarketChange?.(e.value as string)}
                />
            </div>
            <span className={`${baseClassName}__during-label`}>during</span>
            <div className={`${baseClassName}__time-period-selector`}>
                <span className={`${baseClassName}__time-period-selector-label`}>Time Period</span>
                <ByzzerSelect
                    className={`${baseClassName}__time-period-select`}
                    name={'timePeriod'}
                    options={timePeriodOptions}
                    value={conditionTimePeriod ?? 'each_time_period'}
                    placeholder={'Select...'}
                    onChange={(e) => onTimePeriodChange?.(e.value as string)}
                />
            </div>
        </div>
        <div className={`${baseClassName}__name-condition`}>
            <ByzzerInput
                className={`${baseClassName}__name-condition-input`}
                name={'conditionName'}
                label={'Name Condition'}
                placeholder={'Enter condition name...'}
                value={conditionName}
                sublabel={undefined}
                onChange={(e: any) => onConditionNameChange?.(e.target.value)}
                button={undefined}
                disabled={false}
                locked={false}
                onIconClick={undefined}
                onButtonClick={undefined}
                onBlur={undefined}
                required={false}
            />
        </div>
    </div>
}
